import React, { useState, useEffect } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import { Upload, Trash2, FileText, Image, X, Eye, AlertCircle, Calendar, Download, Copy, Edit, Plus, DollarSign, Shield, Lock, Unlock, Search, Users, UserPlus, UserMinus, CheckCircle } from 'lucide-react';
import axios from 'axios';

const UploadPDF = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [existingPosterUrl, setExistingPosterUrl] = useState(null);
  const [pdfName, setPdfName] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [price, setPrice] = useState('');
  const [message, setMessage] = useState('');
  const [pdfs, setPdfs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // Custom Access States
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [existingCustomAccessUsers, setExistingCustomAccessUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [grantingAccess, setGrantingAccess] = useState(false);
  const [customAccessPrice, setCustomAccessPrice] = useState('');
  
  // Doc Access States
  const [docAccess, setDocAccess] = useState({
    LongTermGrowthPortfolio: 0,
    AllWeatherPortfolio: 0,
    DividendGrowthPortfolio: 0,
    HeatMapDashboard: 0,
    IntradayCallsDashboard: 0,
    RegisteredRAPlateform: 0,
    SEBIMonthlyRecommendations: 0,
    FundamentalScreener: 0,
    ReturnScreener: 0,
    IPOResearch: 0
  });

  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    pdfUrl: ''
  });

  // Fetch all PDFs
  const fetchPDFs = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get('https://api.marketmaestroovip.in/api/pdf');
      setPdfs(res.data);
    } catch (err) {
      console.error('Error fetching PDFs:', err);
      setPdfs([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch users for dropdown
  const fetchUsers = async () => {
    try {
      const res = await axios.get('https://api.marketmaestroovip.in/api/pdf/users/active');
      if (res.data && res.data.success) {
        setAllUsers(res.data.data);
        setFilteredUsers(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Search users
  const searchUsers = async (searchTerm) => {
    if (!searchTerm.trim()) {
      fetchUsers();
      return;
    }
    try {
      const res = await axios.get(`https://api.marketmaestroovip.in/api/pdf/users/search?search=${encodeURIComponent(searchTerm)}`);
      if (res.data && res.data.success) {
        setFilteredUsers(res.data.data);
      }
    } catch (err) {
      console.error('Error searching users:', err);
    }
  };

  // Fetch existing custom access users for a document
  const fetchCustomAccessUsers = async (documentId) => {
    try {
      const res = await axios.get(`https://api.marketmaestroovip.in/api/pdf/custom-access/${documentId}/users`);
      if (res.data && res.data.success) {
        setExistingCustomAccessUsers(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching custom access users:', err);
      setExistingCustomAccessUsers([]);
    }
  };

  useEffect(() => {
    fetchPDFs();
    fetchUsers();
  }, []);

  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (userSearchTerm !== undefined) {
        searchUsers(userSearchTerm);
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [userSearchTerm]);

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setPdfName(file.name);
      setMessage('');
    } else {
      setMessage('Please select a valid PDF file');
      e.target.value = '';
    }
  };

  const handlePosterChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setPosterFile(file);
      setExistingPosterUrl(null);
      setPosterPreview(URL.createObjectURL(file));
      setMessage('');
    } else {
      setMessage('Please select a valid image file');
      e.target.value = '';
    }
  };

  const resetForm = () => {
    setPdfFile(null);
    setPosterFile(null);
    setPosterPreview(null);
    setExistingPosterUrl(null);
    setPdfName('');
    setTitle('');
    setContent('');
    setPrice('');
    setEditingId(null);
    setMessage('');
    setSelectedUsers([]);
    setExistingCustomAccessUsers([]);
    setUserSearchTerm('');
    setCustomAccessPrice('');
    setDocAccess({
      LongTermGrowthPortfolio: 0,
      AllWeatherPortfolio: 0,
      DividendGrowthPortfolio: 0,
      HeatMapDashboard: 0,
      IntradayCallsDashboard: 0,
      RegisteredRAPlateform: 0,
      SEBIMonthlyRecommendations: 0,
      FundamentalScreener: 0,
      ReturnScreener: 0,
      IPOResearch: 0
    });
    const pdfInput = document.getElementById('pdf-upload');
    const posterInput = document.getElementById('poster-upload');
    if (pdfInput) pdfInput.value = '';
    if (posterInput) posterInput.value = '';
  };

  const handleEdit = async (pdf) => {
    resetForm();
    
    setEditingId(pdf.id);
    setShowForm(true);
    
    setTitle(pdf.title || '');
    setContent(pdf.content || '');
    setPrice(pdf.price !== null && pdf.price > 0 ? pdf.price.toString() : '');
    
    if (pdf.poster) {
      setExistingPosterUrl(`https://api.marketmaestroovip.in/${pdf.poster}`);
    }
    
    // Fetch doc_access
    try {
      const res = await axios.get(`https://api.marketmaestroovip.in/api/pdf/doc-access/${pdf.id}`);
      if (res.data && res.data.success) {
        setDocAccess({
          LongTermGrowthPortfolio: res.data.data.LongTermGrowthPortfolio || 0,
          AllWeatherPortfolio: res.data.data.AllWeatherPortfolio || 0,
          DividendGrowthPortfolio: res.data.data.DividendGrowthPortfolio || 0,
          HeatMapDashboard: res.data.data.HeatMapDashboard || 0,
          IntradayCallsDashboard: res.data.data.IntradayCallsDashboard || 0,
          RegisteredRAPlateform: res.data.data.RegisteredRAPlateform || 0,
          SEBIMonthlyRecommendations: res.data.data.SEBIMonthlyRecommendations || 0,
          FundamentalScreener: res.data.data.FundamentalScreener || 0,
          ReturnScreener: res.data.data.ReturnScreener || 0,
          IPOResearch: res.data.data.IPOResearch || 0
        });
      }
    } catch (error) {
      console.error('Error fetching doc access:', error);
    }
    
    // Fetch existing custom access users for this document
    await fetchCustomAccessUsers(pdf.id);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle user selection from dropdown
  const handleSelectUser = (user) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers([...selectedUsers, { id: user.id, username: user.username, email: user.email, phone: user.phone }]);
    }
    setUserSearchTerm('');
    setShowUserDropdown(false);
  };

  // Remove user from selected list
  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  // Remove existing custom access user
  const handleRemoveExistingAccess = async (accessId, userId, username) => {
    if (!window.confirm(`Remove custom access for ${username}?`)) return;
    
    try {
      await axios.delete(`https://api.marketmaestroovip.in/api/pdf/custom-access/${editingId}/${userId}`);
      setExistingCustomAccessUsers(existingCustomAccessUsers.filter(u => u.id !== accessId && u.user_id !== userId));
      setMessage(`Access removed for ${username}`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Error removing access:', err);
      setMessage('Failed to remove access');
    }
  };

  // Grant custom access to selected users
  const handleGrantCustomAccess = async () => {
    if (selectedUsers.length === 0) {
      setMessage('Please select at least one user');
      return;
    }
    
    setGrantingAccess(true);
    
    try {
      const user_ids = selectedUsers.map(u => u.id);
      const amount = customAccessPrice && parseFloat(customAccessPrice) > 0 ? parseFloat(customAccessPrice) : null;
      
      const res = await axios.post('https://api.marketmaestroovip.in/api/pdf/custom-access/grant-multiple', {
        document_id: editingId,
        user_ids: user_ids,
        amount: amount
      });
      
      if (res.data && res.data.success) {
        setMessage(res.data.message);
        await fetchCustomAccessUsers(editingId);
        setSelectedUsers([]);
        setCustomAccessPrice('');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error granting access:', err);
      setMessage(err.response?.data?.message || 'Failed to grant access');
    } finally {
      setGrantingAccess(false);
    }
  };

  const formatPrice = (priceValue) => {
    if (!priceValue || priceValue === 0) return 'Free';
    return `₹${parseFloat(priceValue).toFixed(2)}`;
  };

  const handleToggleChange = (field) => {
    setDocAccess(prev => ({
      ...prev,
      [field]: prev[field] === 1 ? 0 : 1
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Form values:', { title, content, price, pdfFile, posterFile, editingId, docAccess });

    if (!title || !title.trim()) {
      setMessage('Please enter a title');
      return;
    }

    if (!content || !content.trim()) {
      setMessage('Please enter content');
      return;
    }

    if (!pdfFile && !editingId) {
      setMessage('Please select a PDF file');
      return;
    }

    setUploading(true);
    setMessage('');

    const formData = new FormData();
    
    formData.append('title', title.trim());
    formData.append('content', content.trim());
    formData.append('price', price && price.trim() ? parseFloat(price).toString() : '0');
    
    formData.append('LongTermGrowthPortfolio', docAccess.LongTermGrowthPortfolio);
    formData.append('AllWeatherPortfolio', docAccess.AllWeatherPortfolio);
    formData.append('DividendGrowthPortfolio', docAccess.DividendGrowthPortfolio);
    formData.append('HeatMapDashboard', docAccess.HeatMapDashboard);
    formData.append('IntradayCallsDashboard', docAccess.IntradayCallsDashboard);
    formData.append('RegisteredRAPlateform', docAccess.RegisteredRAPlateform);
    formData.append('SEBIMonthlyRecommendations', docAccess.SEBIMonthlyRecommendations);
    formData.append('FundamentalScreener', docAccess.FundamentalScreener);
    formData.append('ReturnScreener', docAccess.ReturnScreener);
    formData.append('IPOResearch', docAccess.IPOResearch);
    
    if (pdfFile) {
      console.log('Appending PDF file:', pdfFile.name);
      formData.append('pdf', pdfFile);
    }
    
    if (posterFile) {
      console.log('Appending poster file:', posterFile.name);
      formData.append('poster', posterFile);
    }

    try {
      let res;
      const config = {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      };

      if (editingId) {
        console.log('Updating PDF with ID:', editingId);
        res = await axios.put(
          `https://api.marketmaestroovip.in/api/pdf/${editingId}`,
          formData,
          config
        );
        setMessage('PDF updated successfully');
      } else {
        console.log('Uploading new PDF');
        res = await axios.post(
          'https://api.marketmaestroovip.in/api/pdf/upload',
          formData,
          config
        );
        setMessage('PDF uploaded successfully');
      }

      console.log('Response:', res.data);
      resetForm();
      fetchPDFs();
      setShowForm(false);
      
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Upload error:', error);
      
      if (error.response?.data?.message) {
        setMessage(error.response.data.message);
      } else if (error.response?.data?.error) {
        setMessage(error.response.data.error);
      } else {
        setMessage('Operation failed. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this PDF?')) return;

    try {
      await axios.delete(`https://api.marketmaestroovip.in/api/pdf/${id}`);
      fetchPDFs();
      setMessage('PDF deleted successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Delete error:', error);
      alert('Delete failed. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' Bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="flex h-screen">
        <Sidebar isOpen={sidebarOpen} />
        
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            <div className="mb-6 md:mb-8 flex justify-between items-center">
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                  PDF Management
                </h1>
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
                  Upload, manage and organize your PDF documents
                </p>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(!showForm);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {showForm ? 'Cancel' : 'Add New PDF'}
              </button>
            </div>

            {/* Upload/Edit Form */}
            {showForm && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6 mb-6">
                <h2 className="text-lg md:text-xl font-semibold mb-4 flex items-center gap-2">
                  {editingId ? <Edit className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
                  {editingId ? 'Edit PDF' : 'Upload New PDF'}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Title Input */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Title *</label>
                    <input
                      type="text"
                      value={title || ''}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-gray-700"
                      placeholder="Enter PDF title"
                      required
                    />
                  </div>

                  {/* Content Input */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Content *</label>
                    <textarea
                      value={content || ''}
                      onChange={(e) => setContent(e.target.value)}
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-gray-700"
                      placeholder="Enter PDF description or content"
                      required
                    />
                  </div>

                  {/* Price Input */}
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      Price
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={price || ''}
                        onChange={(e) => setPrice(e.target.value)}
                        className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-gray-700"
                        placeholder="0.00 (Free if empty)"
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Leave empty or enter 0 for free PDFs
                    </p>
                  </div>

                  {/* PDF Upload Area */}
                  <div>
                    <label className="block text-sm font-medium mb-2">PDF Document {!editingId && '*'}</label>
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-red-500 transition-colors">
                      <input
                        type="file"
                        id="pdf-upload"
                        accept=".pdf"
                        onChange={handlePdfChange}
                        className="hidden"
                      />
                      <label htmlFor="pdf-upload" className="cursor-pointer block">
                        <div className="flex flex-col items-center">
                          <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mb-4">
                            <FileText className="w-8 h-8 text-red-600 dark:text-red-300" />
                          </div>
                          <p className="text-lg font-medium mb-2">
                            {pdfFile ? pdfName : (editingId ? 'Click to change PDF (optional)' : 'Click to select PDF')}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            PDF files only, max 10MB
                          </p>
                          <div className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition duration-200 inline-block">
                            {pdfFile ? 'Change PDF' : (editingId ? 'Update PDF (optional)' : 'Select PDF')}
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Poster Upload Area */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Poster Image (Optional)</label>
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                      <input
                        type="file"
                        id="poster-upload"
                        accept="image/*"
                        onChange={handlePosterChange}
                        className="hidden"
                      />
                      <label htmlFor="poster-upload" className="cursor-pointer block">
                        <div className="flex flex-col items-center">
                          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-4">
                            <Image className="w-8 h-8 text-blue-600 dark:text-blue-300" />
                          </div>
                          <p className="text-lg font-medium mb-2">
                            {posterFile ? posterFile.name : (editingId ? 'Click to change poster' : 'Click to select poster')}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            JPEG, PNG, GIF, WEBP, max 5MB
                          </p>
                          <div className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition duration-200 inline-block">
                            {posterFile ? 'Change Poster' : (editingId ? 'Update Poster' : 'Select Poster')}
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Custom Access Section - Only show when editing */}
                  {editingId && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Users className="w-5 h-5 text-blue-600" />
                        <h3 className="text-lg font-semibold">Custom User Access</h3>
                      </div>
                      
                      {/* Existing Custom Access Users */}
                      {existingCustomAccessUsers.length > 0 && (
                        <div className="mb-6">
                          <label className="block text-sm font-medium mb-3">Users with Custom Access</label>
                          <div className="space-y-2">
                            {existingCustomAccessUsers.map((user) => (
                              <div key={user.access_id || user.user_id} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                <div>
                                  <p className="font-medium">{user.username}</p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">{user.email} | {user.phone}</p>
                                  <p className="text-xs text-green-600 dark:text-green-400">Access granted: {formatDate(user.access_granted_at)}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveExistingAccess(user.access_id, user.user_id, user.username)}
                                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg transition"
                                  title="Remove Access"
                                >
                                  <UserMinus className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Add New Custom Access */}
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <label className="block text-sm font-medium mb-3 flex items-center gap-2">
                          <UserPlus className="w-4 h-4" />
                          Grant Custom Access to Users
                        </label>
                        
                        {/* User Search Dropdown */}
                        <div className="relative mb-4">
                          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
                            <Search className="w-5 h-5 ml-3 text-gray-400" />
                            <input
                              type="text"
                              value={userSearchTerm}
                              onChange={(e) => {
                                setUserSearchTerm(e.target.value);
                                setShowUserDropdown(true);
                              }}
                              onFocus={() => setShowUserDropdown(true)}
                              placeholder="Search users by name, email, or phone..."
                              className="flex-1 px-3 py-2 bg-transparent focus:outline-none"
                            />
                          </div>
                          
                          {showUserDropdown && filteredUsers.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {filteredUsers.map((user) => (
                                <button
                                  key={user.id}
                                  type="button"
                                  onClick={() => handleSelectUser(user)}
                                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                                  disabled={selectedUsers.some(u => u.id === user.id) || existingCustomAccessUsers.some(u => u.user_id === user.id)}
                                >
                                  <div>
                                    <p className="font-medium">{user.username}</p>
                                    <p className="text-sm text-gray-500">{user.email} | {user.phone}</p>
                                  </div>
                                  {(selectedUsers.some(u => u.id === user.id) || existingCustomAccessUsers.some(u => u.user_id === user.id)) && (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Custom Access Price */}
                        <div className="mb-4 hidden">
                          <label className="block text-sm font-medium mb-2">Custom Price (Optional)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={customAccessPrice}
                            onChange={(e) => setCustomAccessPrice(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                            placeholder="Leave empty to use document price"
                          />
                          <p className="text-xs text-gray-500 mt-1">Set custom price for these users (optional)</p>
                        </div>
                        
                        {/* Selected Users List */}
                        {selectedUsers.length > 0 && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Selected Users ({selectedUsers.length})</label>
                            <div className="space-y-2">
                              {selectedUsers.map((user) => (
                                <div key={user.id} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                  <div>
                                    <p className="font-medium">{user.username}</p>
                                    <p className="text-sm text-gray-500">{user.email}</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveUser(user.id)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Grant Access Button */}
                        {selectedUsers.length > 0 && (
                          <button
                            type="button"
                            onClick={handleGrantCustomAccess}
                            disabled={grantingAccess}
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2"
                          >
                            {grantingAccess ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Granting Access...
                              </>
                            ) : (
                              <>
                                <UserPlus className="w-4 h-4" />
                                Grant Custom Access to {selectedUsers.length} User(s)
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Doc Access Toggle Section */}
                  <div>
                    <label className="block text-sm font-medium mb-4 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Document Access Permissions
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Toggle ON to grant access to this document for specific user roles
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* LongTermGrowthPortfolio */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium">Long Term Growth Portfolio</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Portfolio Access</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleChange('LongTermGrowthPortfolio')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            docAccess.LongTermGrowthPortfolio === 1 ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              docAccess.LongTermGrowthPortfolio === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* AllWeatherPortfolio */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium">All Weather Portfolio</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Portfolio Access</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleChange('AllWeatherPortfolio')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            docAccess.AllWeatherPortfolio === 1 ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              docAccess.AllWeatherPortfolio === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* DividendGrowthPortfolio */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium">Dividend Growth Portfolio</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Portfolio Access</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleChange('DividendGrowthPortfolio')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            docAccess.DividendGrowthPortfolio === 1 ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              docAccess.DividendGrowthPortfolio === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* HeatMapDashboard */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium">Heat Map Dashboard</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Dashboard Access</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleChange('HeatMapDashboard')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            docAccess.HeatMapDashboard === 1 ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              docAccess.HeatMapDashboard === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* IntradayCallsDashboard */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium">Intraday Calls Dashboard</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Dashboard Access</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleChange('IntradayCallsDashboard')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            docAccess.IntradayCallsDashboard === 1 ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              docAccess.IntradayCallsDashboard === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* RegisteredRAPlateform */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium">Registered RA Platform</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Platform Access</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleChange('RegisteredRAPlateform')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            docAccess.RegisteredRAPlateform === 1 ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              docAccess.RegisteredRAPlateform === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* SEBIMonthlyRecommendations */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium">SEBI Monthly Recommendations</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Recommendations Access</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleChange('SEBIMonthlyRecommendations')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            docAccess.SEBIMonthlyRecommendations === 1 ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              docAccess.SEBIMonthlyRecommendations === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* FundamentalScreener */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium">Fundamental Screener</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Screener Access</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleChange('FundamentalScreener')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            docAccess.FundamentalScreener === 1 ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              docAccess.FundamentalScreener === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* ReturnScreener */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium">Return Screener</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Screener Access</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleChange('ReturnScreener')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            docAccess.ReturnScreener === 1 ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              docAccess.ReturnScreener === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* IPOResearch */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium">IPO Research</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Research Access</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleChange('IPOResearch')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            docAccess.IPOResearch === 1 ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              docAccess.IPOResearch === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Previews Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pdfFile && (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-medium flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Selected PDF
                          </h3>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <FileText className="w-8 h-8 text-red-500" />
                            <div className="flex-1">
                              <p className="font-medium">{pdfName}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {formatFileSize(pdfFile.size)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show existing poster preview when editing */}
                    {!posterFile && existingPosterUrl && !posterPreview && (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-medium flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            Current Poster
                          </h3>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex justify-center">
                          <img
                            src={existingPosterUrl}
                            alt="Current Poster"
                            className="max-h-32 max-w-full object-contain rounded"
                          />
                        </div>
                      </div>
                    )}

                    {/* Show new poster preview when selected */}
                    {posterPreview && (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-medium flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            New Poster Preview
                          </h3>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex justify-center">
                          <img
                            src={posterPreview}
                            alt="Poster Preview"
                            className="max-h-32 max-w-full object-contain rounded"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-4 flex-wrap">
                    <button
                      type="submit"
                      disabled={uploading || (!pdfFile && !editingId) || !title?.trim() || !content?.trim()}
                      className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition duration-200 ${
                        !uploading && ((pdfFile || editingId) && title?.trim() && content?.trim())
                          ? 'bg-red-600 hover:bg-red-700 text-white' 
                          : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          {editingId ? 'Updating...' : 'Uploading...'}
                        </>
                      ) : (
                        <>
                          {editingId ? <Edit className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
                          {editingId ? 'Update PDF' : 'Upload PDF'}
                        </>
                      )}
                    </button>

                    {(pdfFile || posterFile || editingId) && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <X className="w-4 h-4" />
                        Clear
                      </button>
                    )}

                    {message && (
                      <div className={`p-3 rounded-lg flex items-center gap-2 ${
                        message.includes('successfully') || message.includes('granted') || message.includes('removed')
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                      }`}>
                        <AlertCircle className="w-5 h-5" />
                        <span>{message}</span>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            )}

            {/* PDFs List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  All PDF Documents ({pdfs.length})
                </h2>
                <button
                  onClick={fetchPDFs}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg flex items-center gap-2"
                >
                  <span className="rotate-90">↻</span>
                  Refresh
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              ) : pdfs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No PDFs uploaded yet</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Click "Add New PDF" to upload your first document
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pdfs.map((pdf) => (
                    <div key={pdf.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4 flex-1">
                            {pdf.poster && (
                              <img
                                src={`https://api.marketmaestroovip.in/${pdf.poster}`}
                                alt={pdf.title}
                                className="w-16 h-16 object-cover rounded"
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-3 flex-wrap mb-1">
                                <h3 className="font-semibold text-lg">{pdf.title}</h3>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  pdf.price && pdf.price > 0 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                                }`}>
                                  {formatPrice(pdf.price)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
                                {pdf.content}
                              </p>
                              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(pdf.created_at)}
                                </span>
                                {pdf.updated_at !== pdf.created_at && (
                                  <span>Updated: {formatDate(pdf.updated_at)}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => window.open(`https://api.marketmaestroovip.in/${pdf.pdf}`, '_blank')}
                              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-lg transition"
                              title="View PDF"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => window.open(`https://api.marketmaestroovip.in/${pdf.pdf}`, '_blank')}
                              className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900 rounded-lg transition"
                              title="Download PDF"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(pdf)}
                              className="p-2 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900 rounded-lg transition"
                              title="Edit PDF"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(pdf.id)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg transition"
                              title="Delete PDF"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 bg-white dark:bg-gray-800">
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="text"
                            readOnly
                            value={`https://api.marketmaestroovip.in/${pdf.pdf}`}
                            className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded"
                            onClick={(e) => e.target.select()}
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`https://api.marketmaestroovip.in/${pdf.pdf}`);
                              alert('PDF URL copied to clipboard!');
                            }}
                            className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded"
                            title="Copy PDF URL"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        {pdf.poster && (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              readOnly
                              value={`https://api.marketmaestroovip.in/${pdf.poster}`}
                              className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded"
                              onClick={(e) => e.target.select()}
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(`https://api.marketmaestroovip.in/${pdf.poster}`);
                                alert('Poster URL copied to clipboard!');
                              }}
                              className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded"
                              title="Copy Poster URL"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </main>

          <Footer />
        </div>
      </div>
    </div>
  );
};

export default UploadPDF;